import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { environment } from '../../../environments/environment';

export interface GeneralledgerGroupOption {
  id: string;
  name: string;
}

/** Detail model for a single general ledger group, used by the editable card. */
export interface GeneralLedgerGroup {
  id: string;
  generalLedger_Group_Name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value: string;
}

interface GeneralledgerGroupApiModel {
  generalLedger_Group_ID: string;
  generalLedger_Group_Name: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  memo_Value?: string | null;
}

export interface GeneralLedgerGroupReference {
  tablename: string;
  count: number;
}

interface GeneralledgerGroupApiPayload {
  generalLedger_Group_ID: string;
  generalLedger_Group_Name: string;
  startDate: string | null;
  endDate: string | null;
  memo_Value: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class GeneralledgerGroupService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/GeneralLedgerGroup';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the general ledger groups for the current context, used to populate
   * the "Grootboekrekeningengroep" dropdown.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getByContext(): Observable<GeneralledgerGroupOption[]> {
    return this.http
      .get<GeneralledgerGroupApiModel[] | null>(this.apiUrl + '/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => ({
            id: item.generalLedger_Group_ID,
            name: item.generalLedger_Group_Name,
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the general ledger groups for the current context with all fields
   * needed for the list view (name, dates and memo).
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getGroupsByContext(): Observable<GeneralLedgerGroup[]> {
    return this.http
      .get<GeneralledgerGroupApiModel[] | null>(this.apiUrl + '/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => ({
            id: item.generalLedger_Group_ID,
            generalLedger_Group_Name: item.generalLedger_Group_Name,
            startDate: item.startDate ?? null,
            endDate: item.endDate ?? null,
            memo_Value: item.memo_Value ?? '',
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single general ledger group by its id with all detail fields.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns null when the API responds with 204 No Content or 404.
   */
  getGeneralLedgerGroupById(id: string): Observable<GeneralLedgerGroup | null> {
    return this.http
      .get<GeneralledgerGroupApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiModelToDetail(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new general ledger group. The id is always forced to the empty
   * guid so the backend can generate a new one.
   * Uses withCredentials so cookies from a different subdomain are included.
   */
  addGeneralLedgerGroup(group: GeneralLedgerGroup): Observable<GeneralLedgerGroup> {
    const payload = this.mapDetailToApiPayload({ ...group, id: EMPTY_GUID });

    return this.http
      .post<GeneralledgerGroupApiModel | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({
          ...group,
          id: response?.generalLedger_Group_ID ?? group.id,
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing general ledger group.
   * Uses withCredentials so cookies from a different subdomain are included.
   */
  updateGeneralLedgerGroup(id: string, group: GeneralLedgerGroup): Observable<GeneralLedgerGroup> {
    const payload = this.mapDetailToApiPayload({ ...group, id });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...group, id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Returns the references that still point to a general ledger group and would
   * block its deletion. Returns an empty array when the API responds with 204 No
   * Content, meaning the group can be deleted safely.
   */
  getReferenceCountForDeleteCheck(
    id: string,
    language = 'nl',
  ): Observable<GeneralLedgerGroupReference[]> {
    return this.http
      .get<GeneralLedgerGroupReference[] | null>(
        `${this.apiUrl}/GetReferenceCountForDeleteCheck/${id}?language=${language}`,
        this.httpOptions,
      )
      .pipe(
        map((data) => data ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Removes a general ledger group by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   */
  removeGeneralLedgerGroup(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private mapApiModelToDetail(item: GeneralledgerGroupApiModel): GeneralLedgerGroup {
    return {
      id: item.generalLedger_Group_ID,
      generalLedger_Group_Name: item.generalLedger_Group_Name,
      startDate: item.startDate ?? null,
      endDate: item.endDate ?? null,
      memo_Value: item.memo_Value ?? '',
    };
  }

  private mapDetailToApiPayload(group: GeneralLedgerGroup): GeneralledgerGroupApiPayload {
    return {
      generalLedger_Group_ID: group.id,
      generalLedger_Group_Name: group.generalLedger_Group_Name,
      startDate: this.formatApiDate(group.startDate),
      endDate: this.formatApiDate(group.endDate),
      memo_Value: group.memo_Value ?? '',
    };
  }

  private formatApiDate(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
